import { Field, Form, Formik } from 'formik';
import React from 'react';
import MaskedInput from 'react-text-mask';
import { IAgentLandingAgent, IAgentLandingAgentData } from 'types/agent-landing-page';
import { ELeadResponseType } from 'types/lead-response';
import * as Yup from 'yup';

import LockOutlinedIcon from '@material-ui/icons/LockOutlined';

import { trackCustomEvent } from 'components/MetaPixelWrapper';

import { IAgentLandingLeadPayload, submitAgentLandingContactAgentForm } from 'utils/api';
import {
  isAgentLandingPageSample, nameRegExp,
} from 'utils/lib';

export const normalizeWebsite = (website?: string): string | undefined => {
  if (!website) return undefined;
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
};

export const normalizeAgents = (data?: IAgentLandingAgentData | null): IAgentLandingAgent[] => {
  if (!data) return [];
  const agents: IAgentLandingAgent[] = [{
    firstName: data.firstName,
    lastName: data.lastName,
    phoneNumber: data.phoneNumber,
    email: data.email,
    website: normalizeWebsite(data.website),
    licenseNumber: data.agentDreNumber,
    pictureUrl: data.agentPictureUrl,
  }];

  if (data.secondProfileFirstName) {
    agents.push({
      firstName: data.secondProfileFirstName,
      lastName: data.secondProfileLastName,
      phoneNumber: data.secondProfilePhoneNumber,
      email: data.secondProfileEmailAddress,
      website: normalizeWebsite(data.secondProfileWebsite),
      licenseNumber: data.secondProfileLicenseNumber,
      pictureUrl: data.secondProfilePictureUrl,
    });
  }
  return agents;
};

export const AgentAvatar = ({ agent, compact = false }: { agent: IAgentLandingAgent; compact?: boolean }) => (
  <div className={`al-agent-identity${compact ? ' al-agent-identity--compact' : ''}`}>
    {agent.pictureUrl ? (
      <img src={agent.pictureUrl} alt={`Portrait of ${agent.firstName} ${agent.lastName || ''}`.trim()} />
    ) : (
      <span className="al-agent-fallback" aria-hidden="true">
        {agent.firstName.charAt(0)}
        {agent.lastName?.charAt(0)}
      </span>
    )}
    <span>
      <strong>
        {agent.firstName}
        {' '}
        {agent.lastName}
      </strong>
      {agent.phoneNumber && <a href={`tel:${agent.phoneNumber}`}>{agent.phoneNumber}</a>}
    </span>
  </div>
);

export const Eyebrow = ({ children, light = false }: { children: React.ReactNode; light?: boolean }) => (
  <p className={`al-eyebrow${light ? ' al-eyebrow--light' : ''}`}>
    <span aria-hidden="true" />
    {children}
  </p>
);

export interface AgentLandingLeadFormProps {
  agentLandingPageToken: string;
  agentData?: IAgentLandingAgentData;
  title: string;
  subtitle?: string;
  submitLabel: string;
  successTitle?: string;
  successMessage?: string;
  propertyAddress?: string;
  zipCode?: string;
  reportContext?: string;
  leadResponseType: ELeadResponseType.AgentLandingPropertyReviewRequest | ELeadResponseType.AgentLandingMarketReportRequest;
  requestOptions?: { value: string; label: string; description?: string }[];
  showMessage?: boolean;
  showPropertyAddress?: boolean;
  showSellingTimeline?: boolean;
  className?: string;
  onSuccess?: () => void;
  formVariant?: 'default' | 'market' | 'report';
  stepLabel?: string;
  privacyText?: string;
}

const defaultRequestOptions = [
  { value: 'Current value review', label: 'A current value range' },
  { value: 'Preparing to sell', label: 'Preparing to sell' },
  { value: 'Understanding my equity', label: 'Understanding my equity' },
  { value: 'Market monitoring', label: 'Just keeping an eye on the market' },
];

export const valuationReportRequestOptions = [
  { value: 'Refined value and next steps', label: 'Refined value', description: 'Next steps for selling' },
  { value: 'Equity options', label: 'Equity options', description: 'Plan with more clarity' },
  { value: 'Selling timeline', label: 'Selling timeline', description: 'Advice on when to act' },
];

const phoneNumberMask = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
const agentLandingPhoneRegExp = /^(?:\([1-9]\d{2}\) \d{3}-\d{4}|[1-9]\d{9})$/;

let leadFormIdCounter = 0;

const AgentLandingLeadForm = ({
  agentLandingPageToken,
  agentData,
  title,
  subtitle,
  submitLabel,
  successTitle,
  successMessage,
  propertyAddress = '',
  zipCode = '',
  reportContext = '',
  leadResponseType,
  requestOptions = defaultRequestOptions,
  showMessage = true,
  showPropertyAddress = true,
  showSellingTimeline = true,
  className = '',
  onSuccess,
  formVariant = 'default',
  stepLabel = 'Takes less than 2 minutes',
  privacyText = 'No spam. No obligation. Your details are never sold.',
}: AgentLandingLeadFormProps) => {
  const [submitted, setSubmitted] = React.useState(false);
  const [apiError, setApiError] = React.useState('');
  const formIdRef = React.useRef('');
  if (!formIdRef.current) {
    leadFormIdCounter += 1;
    formIdRef.current = `al-lead-form-${leadFormIdCounter}`;
  }
  const fieldId = (name: string) => `${formIdRef.current}-${name}`;
  const isTeam = normalizeAgents(agentData).length > 1;
  const isMarketReportRequest = leadResponseType === ELeadResponseType.AgentLandingMarketReportRequest;
  const resolvedSuccessTitle = successTitle || (isMarketReportRequest
    ? 'Thank you — your valuation request is on its way.'
    : 'Thank you — your property review request is on its way.');
  const resolvedSuccessMessage = successMessage
    || (isMarketReportRequest
      ? `${isTeam ? 'We’ll' : 'I’ll'} apply the local market data to your property and follow up with a more specific valuation.`
      : `${isTeam ? 'We’ll' : 'I’ll'} review the property details you shared and follow up with a thoughtful local perspective.`);

  if (submitted) {
    return (
      <div className={`al-lead-form al-form-success ${className}`} role="status" tabIndex={-1}>
        <span className="al-thanks-icon" aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 12 4 4L19 6" />
          </svg>
        </span>
        <Eyebrow>Message received</Eyebrow>
        <h2>{resolvedSuccessTitle}</h2>
        <p>{resolvedSuccessMessage}</p>
      </div>
    );
  }

  return (
    <Formik
      initialValues={{
        fullName: '',
        phoneNumber: '',
        email: '',
        propertyAddress,
        requestType: requestOptions[0]?.value || '',
        sellingTimeline: '',
        message: '',
        agreesToTerms: true,
      }}
      enableReinitialize
      validationSchema={Yup.object({
        fullName: Yup.string().required('Enter your full name').matches(nameRegExp, 'Enter a valid name'),
        phoneNumber: Yup.string().required('Enter your phone number').matches(agentLandingPhoneRegExp, 'Enter a valid phone number'),
        email: Yup.string().email('Enter a valid email address').required('Enter your email address'),
        propertyAddress: showPropertyAddress
          ? Yup.string().trim().max(160).required('Enter the property address')
          : Yup.string().trim().max(160),
        requestType: Yup.string().required('Choose what would be most helpful'),
        sellingTimeline: Yup.string().max(100),
        message: Yup.string().max(500, 'Keep your message under 500 characters'),
        agreesToTerms: Yup.boolean().oneOf([true], 'Consent is required'),
      })}
      onSubmit={async (values, helpers) => {
        setApiError('');
        try {
          trackCustomEvent(leadResponseType === ELeadResponseType.AgentLandingMarketReportRequest
            ? 'MarketReportRequested'
            : 'PropertyReviewRequested');
          if (!isAgentLandingPageSample(agentLandingPageToken)) {
            const payload: IAgentLandingLeadPayload = {
              fullName: values.fullName.trim(),
              phoneNumber: values.phoneNumber,
              email: values.email.trim(),
              propertyAddress: values.propertyAddress.trim() || undefined,
              requestType: values.requestType,
              sellingTimeline: values.sellingTimeline || undefined,
              message: values.message.trim() || undefined,
              zipCode: zipCode || undefined,
              reportContext: reportContext || undefined,
              leadResponseType,
              recaptchaValue: '',
            };
            await submitAgentLandingContactAgentForm(agentLandingPageToken, payload);
          }
          setSubmitted(true);
          onSuccess?.();
        } catch (error) {
          setApiError('Your request could not be sent. Please check your details and try again.');
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ errors, touched, isSubmitting }) => {
        const errorFor = (name: keyof typeof errors) => touched[name] && errors[name];
        return (
          <Form className={`al-lead-form ${className}`} noValidate>
            <p className="al-form-step">{stepLabel}</p>
            <h2>{title}</h2>
            {subtitle && <p className="al-form-subtitle">{subtitle}</p>}
            {formVariant === 'default' ? (
              <>
                <label htmlFor={fieldId('full-name')}>
                  <span className="al-field-label">
                    Full name
                    {' '}
                    <b aria-hidden="true">*</b>
                  </span>
                  <Field id={fieldId('full-name')} name="fullName" required autoComplete="name" placeholder="Your full name" aria-invalid={!!errorFor('fullName')} />
                  {errorFor('fullName') && <small className="al-field-error">{errors.fullName}</small>}
                </label>
                <div className="al-form-row">
                  <label htmlFor={fieldId('phone-number')}>
                    <span className="al-field-label">
                      Phone number
                      {' '}
                      <b aria-hidden="true">*</b>
                    </span>
                    <Field
                      id={fieldId('phone-number')}
                      name="phoneNumber"
                      required
                      as={MaskedInput}
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      placeholder="(555) 555-0123"
                      aria-invalid={!!errorFor('phoneNumber')}
                      mask={phoneNumberMask}
                      guide={false}
                      maxLength={14}
                    />
                    {errorFor('phoneNumber') && <small className="al-field-error">{errors.phoneNumber}</small>}
                  </label>
                  <label htmlFor={fieldId('email')}>
                    <span className="al-field-label">
                      Email address
                      {' '}
                      <b aria-hidden="true">*</b>
                    </span>
                    <Field id={fieldId('email')} name="email" required type="email" autoComplete="email" placeholder="you@example.com" aria-invalid={!!errorFor('email')} />
                    {errorFor('email') && <small className="al-field-error">{errors.email}</small>}
                  </label>
                </div>
              </>
            ) : (
              <>
                <div className="al-form-row">
                  <label htmlFor={fieldId('full-name')}>
                    <span className="al-field-label">
                      Full name
                      {' '}
                      <b aria-hidden="true">*</b>
                    </span>
                    <Field id={fieldId('full-name')} name="fullName" required autoComplete="name" placeholder="Your full name" aria-invalid={!!errorFor('fullName')} />
                    {errorFor('fullName') && <small className="al-field-error">{errors.fullName}</small>}
                  </label>
                  <label htmlFor={fieldId('phone-number')}>
                    <span className="al-field-label">
                      Phone number
                      {' '}
                      <b aria-hidden="true">*</b>
                    </span>
                    <Field
                      id={fieldId('phone-number')}
                      name="phoneNumber"
                      required
                      as={MaskedInput}
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      placeholder="(555) 555-0123"
                      aria-invalid={!!errorFor('phoneNumber')}
                      mask={phoneNumberMask}
                      guide={false}
                      maxLength={14}
                    />
                    {errorFor('phoneNumber') && <small className="al-field-error">{errors.phoneNumber}</small>}
                  </label>
                </div>
                <label htmlFor={fieldId('email')}>
                  <span className="al-field-label">
                    Email address
                    {' '}
                    <b aria-hidden="true">*</b>
                  </span>
                  <Field id={fieldId('email')} name="email" required type="email" autoComplete="email" placeholder="you@example.com" aria-invalid={!!errorFor('email')} />
                  {errorFor('email') && <small className="al-field-error">{errors.email}</small>}
                </label>
              </>
            )}
            {showPropertyAddress && (
              <label htmlFor={fieldId('property-address')}>
                <span className="al-field-label">
                  Property address
                  {' '}
                  <b aria-hidden="true">*</b>
                </span>
                <Field id={fieldId('property-address')} name="propertyAddress" required autoComplete="street-address" placeholder="Street address, city, ZIP" aria-invalid={!!errorFor('propertyAddress')} />
                {errorFor('propertyAddress') && <small className="al-field-error">{errors.propertyAddress}</small>}
              </label>
            )}
            {formVariant === 'report' ? (
              <fieldset className="al-choice-fieldset">
                <legend>What would help most right now?</legend>
                <div className="al-choice-grid">
                  {requestOptions.map((option, index) => (
                    <label key={option.value} htmlFor={fieldId(`request-option-${index}`)}>
                      <Field id={fieldId(`request-option-${index}`)} type="radio" name="requestType" value={option.value} />
                      <span>
                        <b>{option.label}</b>
                        <small>{option.description || 'Personalized guidance'}</small>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : formVariant === 'default' ? (
              <label htmlFor={fieldId('request-type')}>
                <span className="al-field-label">What would be most helpful?</span>
                <Field id={fieldId('request-type')} name="requestType" as="select">
                  {requestOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </Field>
              </label>
            ) : null}
            {formVariant === 'default' && showSellingTimeline && (
              // Formik renders the associated select with the matching id.
              // eslint-disable-next-line jsx-a11y/label-has-associated-control
              <label htmlFor={fieldId('selling-timeline')}>
                <span className="al-field-label">Selling timeline</span>
                <small className="al-optional">Optional</small>
                <Field id={fieldId('selling-timeline')} name="sellingTimeline" as="select">
                  <option value="">Not sure yet</option>
                  <option value="0-3 months">Within 3 months</option>
                  <option value="3-6 months">3–6 months</option>
                  <option value="6-12 months">6–12 months</option>
                  <option value="12+ months">More than a year</option>
                </Field>
              </label>
            )}
            {showMessage && (
              <label htmlFor={fieldId('message')}>
                <span className="al-field-label">
                  Anything
                  {' '}
                  {isTeam ? 'we' : 'I'}
                  {' '}
                  should know?
                </span>
                <Field id={fieldId('message')} name="message" as="textarea" rows={3} placeholder="Timing, upgrades, questions..." />
                {errorFor('message') && <small className="al-field-error">{errors.message}</small>}
              </label>
            )}
            <label className="al-consent" htmlFor={fieldId('consent')}>
              <Field
                id={fieldId('consent')}
                name="agreesToTerms"
                type="checkbox"
                aria-describedby={errorFor('agreesToTerms') ? fieldId('consent-error') : undefined}
              />
              <span>
                I agree to the
                {' '}
                <a href="https://harvist.com/privacy-terms-and-conditions/" target="_blank" rel="noreferrer">Terms of Service</a>
                {formVariant === 'report' ? '.' : ' and to be contacted about my request.'}
              </span>
            </label>
            {touched.agreesToTerms && errors.agreesToTerms && (
              <small id={fieldId('consent-error')} className="al-field-error al-consent-error" role="alert">
                {errors.agreesToTerms}
              </small>
            )}
            {apiError && <div className="al-api-error" role="alert">{apiError}</div>}
            <button className="al-primary-button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : submitLabel}
              <span aria-hidden="true">→</span>
            </button>
            <p className="al-privacy">
              <LockOutlinedIcon />
              {' '}
              {privacyText}
            </p>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AgentLandingLeadForm;
